import React from "react";
import { useState, useRef, useEffect } from "react";
import {
    Box, Typography, Chip, Button, IconButton, Grid, TextField, Stack, Popper, Grow, Paper, ClickAwayListener, Tabs, Tab,
    Menu, MenuItem, Collapse
} from "@mui/material";
import moment from "moment";
import TableComponent from "components/grid";
import StatusBadge from "components/badge/StatusBadge";
import RiskSeverityBadge from "components/badge/RiskSeverityBadge";
import CustomPagination from "components/CustomPagination";
import { ReactComponent as EditIcon } from "assets/icons/EditIcon.svg";
import { LuEye, LuRefreshCw, LuSearch } from "react-icons/lu";
import { BiListUl } from "react-icons/bi";
import { MdOutlineFileDownload, MdOutlineFilterAlt } from "react-icons/md";
import { FaPlus } from "react-icons/fa6";
import Select from "react-select";
import CustomButton from "components/Button";
import AddNCRLog from "./AddNCRLog";
import EditNCRLog from "./EditNCRLog";
import ViewNCRLog from "./ViewNCRLog";
import { useLazyGetNCRLogListQuery, useGetNCRStatusCountQuery, useGetNcrKpiMetricsQuery, useLazyGetExportNCRLogListQuery } from "store/slices/apis/ncrLogApiSlice";
import { useParams } from "react-router-dom";
import { riskStatus, riskCategories, riskSeverity, riskResponsible } from "helpers/riskOptions";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CalenderSVG from "assets/logo/Calendar";
import { useMemo } from "react";
import OpenClosedRiskChart from "./OpenClosedRiskChart";
import AgeingAnalysisRiskChart from "./AgeingAnalysisRiskChart";
import GroupCategoryBarChart from "./GroupCategoryBarChart";
import AgeingAnalysisChart from "./AgeingAnalysisChart";
import Ncrkpi from "./Ncrkpi";
import { useGetAllContactsQuery } from "store/slices/apis/contactSlice";
import { mapAndSortAlphabetically } from "utils/mapSortHelper";
import { usePermission } from "hooks/usePermission";
import { NCR_LOG_CREATE, NCR_LOG_EDIT, NCR_LOG_VIEW } from "helpers/permissionConfig";
import ProjectLastUpdatedOn from "components/ProjectLastUpdatedOn";
import TrackerPageHeader from "components/TrackerPageHeader";
import { useGetLastUpdatedOnStatusQuery } from "store/slices/apis/projectStatusApiSlice";
import { DEFAULT_DATE_FORMAT } from "config";
import useExcelExport from "hooks/useExcelExport";
import { useGetMasterDataQuery } from "store/slices/apis/masterConfigation";

const normalize = (value) =>
    (value || "")
        .toString()
        .trim()
        .toUpperCase();

const ncrResponse = [
    { label: "Yes", value: true },
    { label: "No", value: false },
];

const NcrLogTracker = () => {
    const [showSummary, setShowSummary] = useState(true);

    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const [menuRow, setMenuRow] = useState(null);

    const [searchTerm, setSearchTerm] = useState("");

    const [ncrLogContact, setNCRLogContact] = useState([]);

    const editAccess = usePermission(NCR_LOG_EDIT);
    const viewAccess = usePermission(NCR_LOG_VIEW);
    const createAccess = usePermission(NCR_LOG_CREATE);

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
    };

    const handleMenuOpen = (event, row) => {
        event.stopPropagation(); // prevent row click if any
        setMenuAnchorEl(event.currentTarget);
        setMenuRow(row);
    };

    const handleMenuClose = () => {
        setMenuAnchorEl(null);
        setMenuRow(null);
    };

    const handleMenuView = () => {
        if (menuRow) {
            handleViewClick(menuRow);
        }
        handleMenuClose();
    };

    const handleMenuEdit = () => {
        if (menuRow) {
            handleEditRisk(menuRow);
        }
        handleMenuClose();
    };
    const { projectId } = useParams();


    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [sortOrder, setSortOrder] = useState('desc');
    const [sortBy, setSortBy] = useState('createdDate');


    const [open, setOpen] = useState(false);
    const [tabValue, setTabValue] = useState(0);
    const anchorRef = useRef(null);
    const [isAddRisk, setIsAddRisk] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [selectedData, setSelectedData] = useState(null);
    const [isView, setIsView] = useState(false);
    const [viewData, setViewData] = useState(null);

    const module = "NCRLog";

    const { data: ncrLogStatusRes } = useGetMasterDataQuery({ module: module, category: 'Status', ProjectId: projectId });
    const ncrLogStatusData = ncrLogStatusRes?.data || [];
    const ncrStatusOptions = (ncrLogStatusData || []).map(p => ({
        value: p?.value,
        label: p?.value,
    }));

    const { data: ncrLogNCRSORes } = useGetMasterDataQuery({ module: module, category: 'NCRSO', ProjectId: projectId });
    const ncrLogNCRSOData = ncrLogNCRSORes?.data || [];
    const ncrSoOptions = (ncrLogNCRSOData || []).map(p => ({
        value: p?.value,
        label: p?.value,
    }));

    const [filters, setFilters] = useState({
        SortBy: sortBy,
        SortOrder: sortOrder,
        PageNumber: pageNumber,
        PageSize: pageSize,
        ProjectId: projectId,
        SearchTerm: "",
        IsResponseSharedWithAuthority: null,
    });

    const { data: ncrStatusData } = useGetNCRStatusCountQuery(projectId);
    const totalCount = ncrStatusData?.data?.data?.total ?? 0;
    const openCount = ncrStatusData?.data?.data?.statusCounts?.Open ?? 0;
    const closedCount = ncrStatusData?.data?.data?.statusCounts?.Closed ?? 0;

    const [trigger, { data: ncrList, isFetching }] = useLazyGetNCRLogListQuery();

    const tableData = Array.isArray(ncrList?.data) ? ncrList?.data : [];

    const extractedCategory = Array.isArray(tableData) && tableData.length > 0
        ? [...new Set(tableData.map(item => item.asset).filter(Boolean))]
        : [];

    const ncrLogCategory = extractedCategory.map(asset => ({
        value: asset,
        label: asset
    }));
    // console.log('ncrcategory', ncrLogCategory)

    const { data: ncrKpis, isFetching: ncrKpisLoading, isError: ncrKpiError } = useGetNcrKpiMetricsQuery({
        projectId,
    });

    const contactListRes = useGetAllContactsQuery();

    const { data: statusDate } = useGetLastUpdatedOnStatusQuery({ ProjectId: projectId });

    useEffect(() => {
        if (contactListRes?.isSuccess) {
            const contacts = contactListRes?.data?.data || [];
            setNCRLogContact(
                mapAndSortAlphabetically(contacts, "name", "email")
            );
        }
    }, [contactListRes])

    const { handleExport, isExporting } = useExcelExport({ exportHook: useLazyGetExportNCRLogListQuery, fileName: "NCRLog_Tracker_Data" });

    const exportNCRLogData = (rows) =>
        rows.map((row, index) => ({
            "S.No": index + 1,
            "ID": row.customId || "-",
            "Letter Number": row.letterNumber || "-",
            "No. Of Observations": row.numberOfObservationsRaised || "-",
            "Asset": row.asset || "-",
            "Letter Ref.": row.letterReference || "-",
            "Area/Item Of Inspection": row.areaOrItemOfInspection || "-",
            "NCR/SO": row.ncRorSO || "-",
            "Date Of Issued Letter": row.dateOfIssuedLetter && moment(row.dateOfIssuedLetter).isValid()
                ? moment(row.dateOfIssuedLetter).format(DEFAULT_DATE_FORMAT)
                : '-',
            "Observations (summary)": row.observationSummary || "-",
            "Remarks/Action Required": row.remarksOrActionRequired || "-",
            "Response Shared": row.isResponseSharedWithAuthority === true ? "Yes" : "No",
            "NCR Status": row.ncrStatus || "-",
            "Date Of Closure": row.dateOfClosure && moment(row.dateOfClosure).isValid()
                ? moment(row.dateOfClosure).format(DEFAULT_DATE_FORMAT)
                : '-',
            "Ageing": row.ageingDays || "-",
            "TAT (In Days)": row.tatInDays || "-",
            "Date Of Response": row.dateOfResponse && moment(row.dateOfResponse).isValid()
                ? moment(row.dateOfResponse).format(DEFAULT_DATE_FORMAT)
                : '-',
            "GRIL Response": row.grilResponse || "-",
            "No. Of Observations Addressed": row.numberOfObservationsAddressed || "-",
            "Reference Letter": row.responseLetterReference || "-",
            "Contractor Name": row.contractorName || "-",
            "Person Responsible": row.personResponsible || "-",
            "Due Date": row.dueDate && moment(row.dueDate).isValid()
                ? moment(row.dueDate).format(DEFAULT_DATE_FORMAT)
                : '-',
        }));

    useEffect(() => {
        trigger(filters);
    }, []);

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            if (searchTerm !== filters.SearchTerm) {
                const updatedFilters = {
                    ...filters,
                    SearchTerm: searchTerm,
                    PageNumber: 1,
                };
                setFilters(updatedFilters);
                trigger(updatedFilters);
                setPageNumber(1);
            }
        }, 500);
        return () => clearTimeout(delayDebounceFn);
    }, [searchTerm]);

    const handlePageChange = (newPage) => {
        setPageNumber(newPage);
        const updatedFilters = {
            ...filters,
            PageNumber: newPage,
        };
        setFilters(updatedFilters);
        trigger(updatedFilters);
    };

    const handlePageSizeChange = (newSize) => {
        setPageSize(newSize);
        setPageNumber(1);
        const updatedFilters = {
            ...filters,
            PageSize: newSize,
            PageNumber: 1,
        };
        setFilters(updatedFilters);
        trigger(updatedFilters);
    };

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);

        const updatedFilters = {
            ...filters,
            PageNumber: 1,
            ...(newValue > 0 ? { NCRStatus: ncrStatusOptions[newValue - 1]?.value } : {}),
        };

        if (newValue === 0 && updatedFilters.NCRStatus) {
            delete updatedFilters.NCRStatus;
        }

        setFilters(updatedFilters);
        trigger(updatedFilters);
        setPageNumber(1);
    };

    const handleApplyFilters = () => {
        const updatedFilters = {
            ...filters,
            PageNumber: 1,
        };

        setFilters(updatedFilters);
        trigger(updatedFilters);
        setPageNumber(1);
        handleClose();
    };

    const handleReset = () => {
        const resetFilters = {
            ProjectId: projectId,
            SortBy: sortBy,
            SortOrder: sortOrder,
            PageNumber: 1,
            PageSize: 10,
            SearchTerm: "",
            IsResponseSharedWithAuthority: null,
        };
        setFilters(resetFilters);
        trigger(resetFilters);
        setPageNumber(1);
        handleClose();
    };

    const handleSelectChange = (selectedOption, fieldName) => {
        const updatedFilters = {
            ...filters,
            [fieldName]: selectedOption?.value ?? null,
            PageNumber: 1,
        };

        setFilters(updatedFilters);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        const updatedFilters = {
            ...filters,
            [name]: value ? `${value}T00:00:00Z` : "",
            PageNumber: 1,
        };

        setFilters(updatedFilters);
    };

    // const handleInputChange1 = (e) => {
    //     const { name, value } = e.target;
    //     const updatedFilters = {
    //         ...filters,
    //         [name]: value,
    //         PageNumber: 1,
    //     };

    //     setFilters(updatedFilters);
    //     //trigger(updatedFilters);
    //     setPageNumber(1);
    // };

    const handleRequestSort = (property) => {
        const isAsc = sortBy === property && sortOrder === 'asc';
        const updatedFilters = {
            ...filters,
            SortBy: property,
            SortOrder: isAsc ? 'desc' : 'asc'
        };
        setFilters(updatedFilters);
        setSortOrder(isAsc ? 'desc' : 'asc');
        setSortBy(property);
        trigger(updatedFilters);
    };

    const handleDelete = (filterType) => {
        const updatedFilters = {
            ...filters,
            [filterType]: "",
            PageNumber: 1
        };

        setFilters(updatedFilters);
        trigger(updatedFilters);
        setPageNumber(1);
    };


    const handleAddRisk = () => {
        setIsAddRisk((prev) => !prev);
    };

    const handleToggle = () => {
        setOpen((prevOpen) => !prevOpen);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleEditRisk = (row) => {
        setIsEdit((prev) => !prev);
        setSelectedData(row);
    };

    const handleEditRiskClose = () => {
        setIsEdit((prev) => !prev);
        setSelectedData(null);
    };

    const handleViewClick = (row) => {
        setIsView((prev) => !prev);
        setViewData(row);
    };

    const handleViewClose = () => {
        setIsView(false);
        setViewData(null);
    };

    const handleToggleSummary = () => {
        setShowSummary((prev) => !prev);
    };

    const highlightText = (text, highlight) => {
        if (!highlight || !text) return text;

        const regex = new RegExp(`(${highlight})`, 'gi');
        const parts = String(text).split(regex);

        return parts.map((part, index) =>
            regex.test(part) ? (
                <span
                    key={index}
                    style={{
                        backgroundColor: '#FFF3CD',
                        color: '#856404',
                        fontWeight: 'bold',
                        padding: '0 2px',
                        borderRadius: '3px'
                    }}
                >
                    {part}
                </span>
            ) : part
        );
    };

    const columns = [
        {
            name: "",
            field: "actions",
            width: "10px",
            sticky: true,
            renderer: (row) => (
                <>
                    {(viewAccess || editAccess) &&
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <IconButton
                                size="small"
                                onClick={(e) => handleMenuOpen(e, row)}
                            >
                                <MoreVertIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    }
                </>
            ),
        },
        {
            name: "ID",
            field: "customId",
            width: "130px",
            sticky: true,
        },
        {
            name: "Letter Number",
            field: "letterNumber",
            width: "200px",
            sticky: true,
            renderer: (row) => highlightText(row.letterNumber, searchTerm),
            // renderer: (row) => (
            //     <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
            //         {/* {highlightText(row?.letterNo, searchTerm)} */}
            //         {row?.letterNo}
            //     </Box>
            // ),
        },
        {
            name: "No. Of Observations",
            field: "numberOfObservationsRaised",
            sticky: true,
        },
        {
            name: "Asset",
            field: "asset",
        },
        {
            name: "Letter Ref.",
            field: "letterReference",
            width: "200px",
        },
        {
            name: "Area/Item Of Inspection",
            field: "areaOrItemOfInspection",
            width: "200px",
        },
        {
            name: "NCR/SO",
            field: "ncRorSO",
        },
        {
            name: "Date Of Issued Letter",
            field: "dateOfIssuedLetter",
            renderer: (row) => (
                row?.dateOfIssuedLetter ? moment.utc(row.dateOfIssuedLetter).format("DD MMM YYYY") : "N/A"
            ),
        },
        {
            name: "Observations (summary)",
            field: "observationSummary",
            width: "360px",
            renderer: (row) => (
                <Box
                    sx={{
                        whiteSpace: "pre-line",
                    }}
                >
                    {highlightText(row?.observationSummary, searchTerm)}
                </Box>
            ),
            // renderer: (row) => (
            //     <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
            //         {row?.observations}
            //     </Box>
            // ),
        },
        {
            name: "Remarks/Action Required",
            field: "remarksOrActionRequired",
            width: "360px",
            renderer: (row) => highlightText(row.remarksOrActionRequired, searchTerm),
            // renderer: (row) => (
            //     <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
            //         {row?.remarks}
            //     </Box>
            // ),
        },
        {
            name: "Response Shared",
            field: "isResponseSharedWithAuthority",
            renderer: (row) => (
                <>{row?.isResponseSharedWithAuthority === true ? "Yes" : "No"}</>
            )
        },
        {
            name: 'NCR Status',
            field: 'ncrStatus',
            renderer: (row) => (
                <StatusBadge status={row?.ncrStatus} />
            )
        },
        {
            name: "Date Of Closure",
            field: "dateOfClosure",
            renderer: (row) => (
                row?.dateOfClosure ? moment.utc(row.dateOfClosure).format("DD MMM YYYY") : "N/A"
            ),
        },
        {
            name: "Ageing",
            field: "ageingDays",
        },
        {
            name: "TAT (In Days)",
            field: "tatInDays",
        },
        {
            name: "Date Of Response",
            field: "dateOfResponse",
            renderer: (row) => (
                row?.dateOfResponse ? moment.utc(row.dateOfResponse).format("DD MMM YYYY") : "N/A"
            ),
        },
        {
            name: "GRIL Response",
            field: "grilResponse",
            width: "200px",
            renderer: (row) => (
                <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    {row?.grilResponse}
                </Box>
            ),
        },
        {
            name: "No. Of Observations Addressed",
            field: "numberOfObservationsAddressed",
        },
        {
            name: "Reference Letter",
            field: "responseLetterReference",
        },
        {
            name: "Contractor Name",
            field: "contractorName",
        },
        {
            name: "Person Responsible",
            field: "personResponsible",
            renderer: (row) => (
                <>
                    {row?.personResponsible?.split(',')?.map((val, key) => (
                        <Typography key={key}>
                            {val}
                        </Typography>
                    ))}
                </>
            ),
        },
        {
            name: "Due Date",
            field: "dueDate",
            renderer: (row) => (
                row?.dueDate ? moment.utc(row.dueDate).format("DD MMM YYYY") : "N/A"
            ),
        },
    ];

    return (
        <>
            <TrackerPageHeader title="NCR Log Tracker" description="Track non-conformance, responses, closure, and ageing.">
                {statusDate?.ncrLog && <ProjectLastUpdatedOn date={statusDate?.ncrLog} />}
            </TrackerPageHeader>
            <Grid container spacing={2} sx={{ p: 2 }}>
                <Grid size={12}>
                    <Ncrkpi data={ncrKpis?.data} isLoading={ncrKpisLoading} isError={ncrKpiError} />
                </Grid>
                <Grid size={12}>
                    <Button
                        variant="text"
                        onClick={handleToggleSummary}
                        sx={{
                            fontFamily: "Outfit, sans-serif",
                            textTransform: "none",
                            fontWeight: 500,
                        }}
                    >
                        {showSummary ? "Hide Summary" : "Show Summary"}
                    </Button>
                </Grid>
                <Grid size={12}>
                    <Collapse in={showSummary} timeout="auto" unmountOnExit>
                        <Grid container spacing={2}>
                            <Grid size={{ md: 6, sm: 12, xs: 12 }}>
                                <GroupCategoryBarChart columns={columns} />
                            </Grid>
                            <Grid size={{ md: 6, sm: 12, xs: 12 }}>
                                <AgeingAnalysisChart columns={columns} />
                            </Grid>
                        </Grid>
                    </Collapse>
                </Grid>

                <Grid size={12}>
                    <Box
                        sx={{
                            width: "100%",
                            borderBottom: "2px solid #ECECEC",
                            overflowX: "auto",
                            whiteSpace: "nowrap",
                            p: 0,
                            display: "flex", alignItems: "center", justifyContent: 'space-between',
                            "-webkit-overflow-scrolling": "touch",
                        }}
                    >
                        <Tabs
                            value={tabValue}
                            onChange={handleTabChange}
                            TabIndicatorProps={{ style: { display: "none" } }}
                            variant="scrollable"
                            scrollButtons={false}
                            sx={{
                                justifyContent: "flex-start !important",
                                alignItems: "flex-start",
                                "& .MuiTabs-flexContainer": {
                                    justifyContent: "flex-start !important",
                                    gap: "30px",
                                },
                            }}
                        >
                            {["All", "Open", "Closed"].map((label, index) => {
                                let count = 0;

                                if (label === "All") {
                                    count = totalCount;
                                }
                                else if (label === "Open") {
                                    count = openCount;
                                }
                                else if (label === "Closed") {
                                    count = closedCount;
                                }

                                return (
                                    <Tab
                                        key={index}
                                        label={
                                            <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                                <Typography
                                                    sx={{
                                                        fontWeight: 500,
                                                        fontSize: "14px",
                                                        lineHeight: "100%",
                                                        color: tabValue === index ? "#0E73F6" : "#7E7E7E",
                                                        fontFamily: "Outfit, sans-serif"
                                                    }}
                                                >
                                                    {label}
                                                </Typography>
                                                {index > 0 &&
                                                    <Chip
                                                        label={count}
                                                        sx={{
                                                            height: "20px",
                                                            fontSize: "14px",
                                                            fontWeight: 500,
                                                            lineHeight: 1,
                                                            backgroundColor: "#E5E9EB",
                                                            color: "#5F5F5F",
                                                        }}
                                                    />
                                                }

                                            </Box>
                                        }
                                        sx={{
                                            textTransform: "none",
                                            minWidth: "auto",
                                            paddingBottom: "14px",
                                            paddingLeft: 0,
                                            paddingRight: 0,
                                            borderBottom: tabValue === index ? "2px solid #0E73F6" : "2px solid transparent",
                                        }}
                                    />
                                );
                            })}
                        </Tabs>
                        {createAccess &&
                            <CustomButton
                                label="Create NCR Log"
                                startIcon={<FaPlus />}
                                color="primary"
                                onClick={handleAddRisk}
                                sx={{
                                    backgroundColor: "#2563EB",
                                    fontFamily: 'Outfit',
                                    fontWeight: 500,
                                    fontSize: '14px',
                                    lineHeight: '100%',
                                    letterSpacing: '0px',
                                }}
                            />
                        }
                    </Box>
                </Grid>

                <Grid size={12}>
                    <Grid container>
                        <Grid size={{ md: 8, sm: 6 }}>
                            <Stack
                                direction="row"
                                spacing={1}
                                flexWrap="wrap"
                                sx={{
                                    padding: "8px 0",
                                    alignItems: "center",
                                    minHeight: "40px"
                                }}
                            >
                                {typeof filters.IsResponseSharedWithAuthority === "boolean" && (
                                    <Chip
                                        label={
                                            <Typography variant="body2">
                                                <strong style={{ color: '#0E73F6' }}>Response Shared:</strong>{" "}
                                                <span style={{ fontWeight: 400, color: "#040405ff" }}>
                                                    {filters.IsResponseSharedWithAuthority ? "Yes" : "No"}
                                                </span>
                                            </Typography>
                                        }
                                        sx={{
                                            backgroundColor: "rgba(25, 118, 210, 0.08)",
                                            border: 0
                                        }}
                                        variant="outlined"
                                        onDelete={() => handleDelete('IsResponseSharedWithAuthority')}
                                    />
                                )}

                                {filters.NCRStatus && (
                                    <Chip
                                        label={
                                            <Typography variant="body2">
                                                <strong style={{ color: '#0E73F6' }}>NCR Status:</strong>{" "}
                                                <span style={{ fontWeight: 400, color: "#6B7280" }}>
                                                    {filters.NCRStatus}
                                                </span>
                                            </Typography>
                                        }
                                        sx={{
                                            backgroundColor: "rgba(25, 118, 210, 0.08)",
                                            border: 0
                                        }}
                                        variant="outlined"
                                        onDelete={() => handleDelete('NCRStatus')}
                                    />
                                )}

                                {filters.NCRorSO && (
                                    <Chip
                                        label={
                                            <Typography variant="body2">
                                                <strong style={{ color: '#0E73F6' }}>NCR/SO:</strong>{" "}
                                                <span style={{ fontWeight: 400, color: "#6B7280" }}>
                                                    {filters.NCRorSO}
                                                </span>
                                            </Typography>
                                        }
                                        sx={{
                                            backgroundColor: "rgba(25, 118, 210, 0.08)",
                                            border: 0
                                        }}
                                        variant="outlined"
                                        onDelete={() => handleDelete('NCRorSO')}
                                    />
                                )}

                                {filters.Asset && (
                                    <Chip
                                        label={
                                            <Typography variant="body2">
                                                <strong style={{ color: '#0E73F6' }}>Asset:</strong>{" "}
                                                <span style={{ fontWeight: 400, color: "#6B7280" }}>
                                                    {filters.Asset}
                                                </span>
                                            </Typography>
                                        }
                                        sx={{
                                            backgroundColor: "rgba(25, 118, 210, 0.08)",
                                            border: 0
                                        }}
                                        variant="outlined"
                                        onDelete={() => handleDelete('Asset')}
                                    />
                                )}

                                {filters?.PersonResponsible &&
                                    <Chip
                                        label={
                                            <Typography variant="body2">
                                                <strong style={{ color: '#0E73F6' }}>Person Responsible:</strong>{" "}
                                                <span style={{ fontWeight: 400, color: "#6B7280" }}>{filters?.PersonResponsible}</span>
                                            </Typography>
                                        }
                                        sx={{
                                            backgroundColor: "rgba(25, 118, 210, 0.08)",
                                            border: 0
                                        }}
                                        variant="outlined"
                                        onDelete={() => handleDelete('PersonResponsible')}
                                    />
                                }

                                {filters.DueDate && (
                                    <Chip
                                        label={
                                            <Typography variant="body2">
                                                <strong style={{ color: '#0E73F6' }}>Due Date:</strong>{" "}
                                                <span style={{ fontWeight: 400, color: "#6B7280" }}>
                                                    {moment(filters.DueDate).format('DD-MM-YYYY')}
                                                </span>
                                            </Typography>
                                        }
                                        sx={{
                                            backgroundColor: "rgba(25, 118, 210, 0.08)",
                                            border: 0
                                        }}
                                        variant="outlined"
                                        onDelete={() => handleDelete('DueDate')}
                                    />
                                )}

                                {filters.DateOfIssuedLetter && (
                                    <Chip
                                        label={
                                            <Typography variant="body2">
                                                <strong style={{ color: '#0E73F6' }}>Issued Date:</strong>{" "}
                                                <span style={{ fontWeight: 400, color: "#6B7280" }}>
                                                    {moment(filters.DateOfIssuedLetter).format('DD-MM-YYYY')}
                                                </span>
                                            </Typography>
                                        }
                                        sx={{
                                            backgroundColor: "rgba(25, 118, 210, 0.08)",
                                            border: 0
                                        }}
                                        variant="outlined"
                                        onDelete={() => handleDelete('DateOfIssuedLetter')}
                                    />
                                )}

                                {filters.DateOfClosure && (
                                    <Chip
                                        label={
                                            <Typography variant="body2">
                                                <strong style={{ color: '#0E73F6' }}>Closure Date:</strong>{" "}
                                                <span style={{ fontWeight: 400, color: "#6B7280" }}>
                                                    {moment(filters.DateOfClosure).format('DD-MM-YYYY')}
                                                </span>
                                            </Typography>
                                        }
                                        sx={{
                                            backgroundColor: "rgba(25, 118, 210, 0.08)",
                                            border: 0
                                        }}
                                        variant="outlined"
                                        onDelete={() => handleDelete('DateOfClosure')}
                                    />
                                )}

                                {filters.DateOfResponse && (
                                    <Chip
                                        label={
                                            <Typography variant="body2">
                                                <strong style={{ color: '#0E73F6' }}>Response Date:</strong>{" "}
                                                <span style={{ fontWeight: 400, color: "#6B7280" }}>
                                                    {moment(filters.DateOfResponse).format('DD-MM-YYYY')}
                                                </span>
                                            </Typography>
                                        }
                                        sx={{
                                            backgroundColor: "rgba(25, 118, 210, 0.08)",
                                            border: 0
                                        }}
                                        variant="outlined"
                                        onDelete={() => handleDelete('DateOfResponse')}
                                    />
                                )}
                            </Stack>
                        </Grid>


                        <Grid size={{ md: 4, sm: 6 }}>
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: { md: "row", xs: "column" },
                                    alignItems: { md: "center", xs: "flex-start" },
                                    justifyContent: 'end',
                                    p: "8px 0px"
                                }}
                            >
                                <TextField
                                    placeholder="Search By Letter No., Remarks & Observations"
                                    value={searchTerm}
                                    onChange={handleSearchChange}
                                    variant="outlined"
                                    size="small"
                                    InputProps={{
                                        startAdornment: <LuSearch style={{ marginRight: '8px', color: '#6B7280' }} />,
                                    }}
                                    sx={{
                                        minWidth: { md: '320px', xs: "auto" },
                                        // width: "100%",
                                        backgroundColor: '#fff',
                                        '& .MuiOutlinedInput-root': { borderRadius: '3px', minHeight: '36px', fontSize: '13px' },
                                        '& .MuiInputBase-input::placeholder': {
                                            fontSize: '0.9rem',
                                        }
                                    }}
                                    type="search"
                                />

                                <IconButton
                                    ref={anchorRef}
                                    onClick={handleToggle}
                                >
                                    <MdOutlineFilterAlt size={24} color="#48535B" />
                                </IconButton>

                                <Popper
                                    open={open}
                                    anchorEl={anchorRef.current}
                                    placement="bottom-end"
                                    transition
                                    disablePortal={false}
                                    sx={{ zIndex: 2000 }}
                                >
                                    {({ TransitionProps }) => (
                                        <Grow {...TransitionProps}>
                                            <Paper
                                                sx={{
                                                    mt: 1,
                                                    p: 2,
                                                    borderRadius: 1,
                                                    boxShadow: "0px 4px 20px rgba(7, 7, 7, 0.08)",
                                                    width: 600,
                                                }}
                                            >
                                                <ClickAwayListener onClickAway={handleClose}>
                                                    <Box
                                                        sx={{
                                                            maxHeight: "50vh",   // popup max height
                                                            overflowY: "auto",
                                                            overflowX: "hidden",
                                                            p: 2,
                                                            borderRadius: "3px",
                                                        }}
                                                    >
                                                        <Grid container spacing={2}>
                                                            <Grid size={{ xs: 12, md: 6 }}>
                                                                <Typography sx={{
                                                                    fontFamily: 'Outfit',
                                                                    fontWeight: 500,
                                                                    fontSize: '16px',
                                                                    lineHeight: '100%',
                                                                    letterSpacing: "0px",
                                                                    mb: 1
                                                                }}>Select Response Shared</Typography>
                                                                <Select
                                                                    name="IsResponseSharedWithAuthority"
                                                                    options={ncrResponse}
                                                                    isClearable
                                                                    placeholder="Select Response Shared"
                                                                    onChange={(option) => handleSelectChange(option, "IsResponseSharedWithAuthority")}
                                                                    value={ncrResponse.find(option => option.value === filters.IsResponseSharedWithAuthority) || null}
                                                                />
                                                            </Grid>

                                                            <Grid size={{ xs: 12, md: 6 }}>
                                                                <Typography sx={{
                                                                    fontFamily: 'Outfit',
                                                                    fontWeight: 500,
                                                                    fontSize: '16px',
                                                                    lineHeight: '100%',
                                                                    letterSpacing: "0px",
                                                                    mb: 1
                                                                }}>Select NCR Status</Typography>
                                                                <Select
                                                                    name="NCRStatus"
                                                                    options={ncrStatusOptions}
                                                                    isClearable
                                                                    placeholder="Select NCR Status"
                                                                    onChange={(option) => handleSelectChange(option, "NCRStatus")}
                                                                    value={ncrStatusOptions.find(option => option.value === filters.NCRStatus) || null}
                                                                />
                                                            </Grid>
                                                            <Grid size={{ xs: 12, md: 6 }}>
                                                                <Typography sx={{
                                                                    fontFamily: 'Outfit',
                                                                    fontWeight: 500,
                                                                    fontSize: '16px',
                                                                    lineHeight: '100%',
                                                                    letterSpacing: "0px",
                                                                    mb: 1
                                                                }}>Select NCR/SO</Typography>
                                                                <Select
                                                                    name="NCRorSO"
                                                                    options={ncrSoOptions}
                                                                    isClearable
                                                                    placeholder="Select NCR/SO"
                                                                    onChange={(option) => handleSelectChange(option, "NCRorSO")}
                                                                    value={ncrSoOptions.find(option => option.value === filters.NCRorSO) || null}
                                                                />
                                                            </Grid>

                                                            <Grid size={{ xs: 12, md: 6 }}>
                                                                <Typography sx={{
                                                                    fontFamily: 'Outfit',
                                                                    fontWeight: 500,
                                                                    fontSize: '16px',
                                                                    lineHeight: '100%',
                                                                    letterSpacing: "0px",
                                                                    mb: 1
                                                                }}>Select Asset</Typography>
                                                                <Select
                                                                    name="Asset"
                                                                    options={ncrLogCategory}
                                                                    isClearable
                                                                    placeholder="Select Asset"
                                                                    onChange={(option) => handleSelectChange(option, "Asset")}
                                                                    value={filters.Asset ? {
                                                                        value: filters.Asset,
                                                                        label: filters.Asset
                                                                    } : null}
                                                                />
                                                            </Grid>

                                                            <Grid size={6}>
                                                                <Typography sx={{
                                                                    fontFamily: 'Outfit',
                                                                    fontWeight: 500,
                                                                    fontSize: '16px',
                                                                    lineHeight: '100%',
                                                                    letterSpacing: "0px",
                                                                    mb: 1
                                                                }}>Person Responsible</Typography>
                                                                <Select
                                                                    name="PersonResponsible"
                                                                    placeholder="Select Person Responsible"
                                                                    value={ncrLogContact.find(opt => opt.value === filters?.PersonResponsible) || null}
                                                                    onChange={(option) => handleSelectChange(option, "PersonResponsible")}
                                                                    options={ncrLogContact}
                                                                    isClearable
                                                                />
                                                            </Grid>

                                                            <Grid size={{ xs: 12, md: 6 }}>
                                                                <Typography sx={{
                                                                    fontFamily: 'Outfit',
                                                                    fontWeight: 500,
                                                                    fontSize: '16px',
                                                                    lineHeight: '100%',
                                                                    letterSpacing: "0px",
                                                                    mb: 1
                                                                }}>Due Date</Typography>
                                                                <TextField
                                                                    type="date"
                                                                    name="DueDate"
                                                                    // onBlur={handleBlur}
                                                                    value={filters.DueDate ? new Date(filters.DueDate).toISOString().split('T')[0] : ""}
                                                                    onChange={handleInputChange}
                                                                    onClick={(e) => {
                                                                        // Trigger the native date picker
                                                                        const input = e.currentTarget.parentElement?.querySelector('input[type="date"]');
                                                                        if (input) input.showPicker?.();
                                                                    }}
                                                                    fullWidth
                                                                    sx={{
                                                                        // ...rightTextStyle,
                                                                        "& input::-webkit-calendar-picker-indicator": {
                                                                            display: "none",
                                                                            WebkitAppearance: "none",
                                                                        },
                                                                        "& input[type='date']": {
                                                                            appearance: "none",
                                                                            WebkitAppearance: "none",
                                                                        },
                                                                        "& .MuiOutlinedInput-root": {
                                                                            "& fieldset": {
                                                                                border: "none",
                                                                            },
                                                                        },
                                                                    }}
                                                                    InputProps={{
                                                                        startAdornment: (
                                                                            <Box sx={{ ml: 2, mr: 1, color: "#433F5E", display: "flex", alignItems: "center", cursor: "pointer" }}

                                                                            >
                                                                                {CalenderSVG}
                                                                            </Box>
                                                                        ),
                                                                        sx: {
                                                                            backgroundColor: "#E7E7EB",
                                                                            height: "40px",
                                                                            width: { xs: "100%", md: "100%" },
                                                                            borderRadius: "3px",
                                                                        },
                                                                    }}
                                                                />
                                                            </Grid>

                                                            <Grid size={{ xs: 12, md: 6 }}>
                                                                <Typography sx={{
                                                                    fontFamily: 'Outfit',
                                                                    fontWeight: 500,
                                                                    fontSize: '16px',
                                                                    lineHeight: '100%',
                                                                    letterSpacing: "0px",
                                                                    mb: 1
                                                                }}>Date Of Issued Letter</Typography>
                                                                <TextField
                                                                    type="date"
                                                                    name="DateOfIssuedLetter"
                                                                    // onBlur={handleBlur}
                                                                    value={filters.DateOfIssuedLetter ? new Date(filters.DateOfIssuedLetter).toISOString().split('T')[0] : ""}
                                                                    onChange={handleInputChange}
                                                                    onClick={(e) => {
                                                                        // Trigger the native date picker
                                                                        const input = e.currentTarget.parentElement?.querySelector('input[type="date"]');
                                                                        if (input) input.showPicker?.();
                                                                    }}
                                                                    fullWidth
                                                                    sx={{
                                                                        // ...rightTextStyle,
                                                                        "& input::-webkit-calendar-picker-indicator": {
                                                                            display: "none",
                                                                            WebkitAppearance: "none",
                                                                        },
                                                                        "& input[type='date']": {
                                                                            appearance: "none",
                                                                            WebkitAppearance: "none",
                                                                        },
                                                                        "& .MuiOutlinedInput-root": {
                                                                            "& fieldset": {
                                                                                border: "none",
                                                                            },
                                                                        },
                                                                    }}
                                                                    InputProps={{
                                                                        startAdornment: (
                                                                            <Box sx={{ ml: 2, mr: 1, color: "#433F5E", display: "flex", alignItems: "center", cursor: "pointer" }}

                                                                            >
                                                                                {CalenderSVG}
                                                                            </Box>
                                                                        ),
                                                                        sx: {
                                                                            backgroundColor: "#E7E7EB",
                                                                            height: "40px",
                                                                            width: { xs: "100%", md: "100%" },
                                                                            borderRadius: "3px",
                                                                        },
                                                                    }}
                                                                />
                                                            </Grid>

                                                            <Grid size={{ xs: 12, md: 6 }}>
                                                                <Typography sx={{
                                                                    fontFamily: 'Outfit',
                                                                    fontWeight: 500,
                                                                    fontSize: '16px',
                                                                    lineHeight: '100%',
                                                                    letterSpacing: "0px",
                                                                    mb: 1
                                                                }}>Date Of Closure</Typography>
                                                                <TextField
                                                                    type="date"
                                                                    name="DateOfClosure"
                                                                    // onBlur={handleBlur}
                                                                    onChange={handleInputChange}
                                                                    value={filters.DateOfClosure ? new Date(filters.DateOfClosure).toISOString().split('T')[0] : ""}
                                                                    onClick={(e) => {
                                                                        // Trigger the native date picker
                                                                        const input = e.currentTarget.parentElement?.querySelector('input[type="date"]');
                                                                        if (input) input.showPicker?.();
                                                                    }}
                                                                    fullWidth
                                                                    sx={{
                                                                        // ...rightTextStyle,
                                                                        "& input::-webkit-calendar-picker-indicator": {
                                                                            display: "none",
                                                                            WebkitAppearance: "none",
                                                                        },
                                                                        "& input[type='date']": {
                                                                            appearance: "none",
                                                                            WebkitAppearance: "none",
                                                                        },
                                                                        "& .MuiOutlinedInput-root": {
                                                                            "& fieldset": {
                                                                                border: "none",
                                                                            },
                                                                        },
                                                                    }}
                                                                    InputProps={{
                                                                        startAdornment: (
                                                                            <Box sx={{ ml: 2, mr: 1, color: "#433F5E", display: "flex", alignItems: "center", cursor: "pointer" }}

                                                                            >
                                                                                {CalenderSVG}
                                                                            </Box>
                                                                        ),
                                                                        sx: {
                                                                            backgroundColor: "#E7E7EB",
                                                                            height: "40px",
                                                                            width: { xs: "100%", md: "100%" },
                                                                            borderRadius: "3px",
                                                                        },
                                                                    }}
                                                                />
                                                            </Grid>

                                                            <Grid size={{ xs: 12, md: 6 }}>
                                                                <Typography sx={{
                                                                    fontFamily: 'Outfit',
                                                                    fontWeight: 500,
                                                                    fontSize: '16px',
                                                                    lineHeight: '100%',
                                                                    letterSpacing: "0px",
                                                                    mb: 1
                                                                }}>Date Of Response</Typography>
                                                                <TextField
                                                                    type="date"
                                                                    name="DateOfResponse"
                                                                    // onBlur={handleBlur}
                                                                    value={filters.DateOfResponse ? new Date(filters.DateOfResponse).toISOString().split('T')[0] : ""}
                                                                    onChange={handleInputChange}
                                                                    onClick={(e) => {
                                                                        // Trigger the native date picker
                                                                        const input = e.currentTarget.parentElement?.querySelector('input[type="date"]');
                                                                        if (input) input.showPicker?.();
                                                                    }}
                                                                    fullWidth
                                                                    sx={{
                                                                        // ...rightTextStyle,
                                                                        "& input::-webkit-calendar-picker-indicator": {
                                                                            display: "none",
                                                                            WebkitAppearance: "none",
                                                                        },
                                                                        "& input[type='date']": {
                                                                            appearance: "none",
                                                                            WebkitAppearance: "none",
                                                                        },
                                                                        "& .MuiOutlinedInput-root": {
                                                                            "& fieldset": {
                                                                                border: "none",
                                                                            },
                                                                        },
                                                                    }}
                                                                    InputProps={{
                                                                        startAdornment: (
                                                                            <Box sx={{ ml: 2, mr: 1, color: "#433F5E", display: "flex", alignItems: "center", cursor: "pointer" }}

                                                                            >
                                                                                {CalenderSVG}
                                                                            </Box>
                                                                        ),
                                                                        sx: {
                                                                            backgroundColor: "#E7E7EB",
                                                                            height: "40px",
                                                                            width: { xs: "100%", md: "100%" },
                                                                            borderRadius: "3px",
                                                                        },
                                                                    }}
                                                                />
                                                            </Grid>

                                                            <Grid size={12}>
                                                                <Box
                                                                    sx={{
                                                                        display: "flex",
                                                                        justifyContent: "flex-end",
                                                                        alignItems: "center",
                                                                    }}
                                                                >
                                                                    <Button onClick={handleReset}
                                                                        sx={{
                                                                            textTransform: "none",
                                                                            color: "#F15744",
                                                                            fontSize: "14px",
                                                                            lineHeight: "100%",
                                                                            fontFamily: "Outfit, sans-serif",
                                                                            borderRadius: "3px",
                                                                            fontWeight: 500,
                                                                            px: "20px",
                                                                            py: "15px",
                                                                        }}>
                                                                        Reset
                                                                    </Button>
                                                                    <Button
                                                                        type="button"
                                                                        onClick={handleApplyFilters}
                                                                        variant="contained"
                                                                        sx={{
                                                                            backgroundColor: "#2563EB",
                                                                            borderRadius: "3px",
                                                                            textTransform: "none",
                                                                            fontWeight: 500,
                                                                            fontSize: "14px",
                                                                            lineHeight: "100%",
                                                                            fontFamily: "Outfit, sans-serif",
                                                                            color: "#FFFFFF",
                                                                            px: "20px",
                                                                            py: "15px",
                                                                        }}
                                                                    >
                                                                        Apply
                                                                    </Button>
                                                                </Box>
                                                            </Grid>
                                                        </Grid>
                                                    </Box>
                                                </ClickAwayListener>
                                            </Paper>
                                        </Grow>
                                    )}
                                </Popper>

                                <IconButton onClick={() =>
                                    handleExport(
                                        filters,
                                        exportNCRLogData
                                    )
                                } disabled={isExporting}>
                                    <MdOutlineFileDownload size={24} color="#48535B" />
                                </IconButton>

                                {/* <IconButton>
                                    <BiListUl size={24} color="#48535B" />
                                </IconButton> */}
                                {/* <IconButton>
                                    <MdOutlineFileDownload size={24} color="#48535B" />
                                </IconButton> */}
                                <IconButton onClick={() => trigger(filters)}>
                                    <LuRefreshCw size={20} color="#48535B" />
                                </IconButton>
                            </Box>
                        </Grid>
                    </Grid>
                </Grid>


                <Grid size={12}>
                    <TableComponent
                        data={ncrList?.data || []}
                        isLoading={isFetching}
                        columns={columns}
                        order={sortOrder}
                        orderBy={sortBy}
                        createSortHandler={handleRequestSort}
                    />
                    <Menu
                        anchorEl={menuAnchorEl}
                        open={Boolean(menuAnchorEl)}
                        onClose={handleMenuClose}
                        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                        transformOrigin={{ vertical: "top", horizontal: "right" }}
                        PaperProps={{
                            elevation: 0,
                            sx: {
                                mt: 1,
                                borderRadius: "3px",
                                minWidth: 140,
                                border: "1px solid #E5E9EB",
                                boxShadow: "0px 10px 30px rgba(15, 23, 42, 0.12)",
                                overflow: "hidden",
                            },
                        }}
                        MenuListProps={{
                            sx: {
                                py: 0.5,
                            },
                        }}
                    >
                        {viewAccess &&
                            <MenuItem
                                onClick={handleMenuView}
                                sx={{
                                    fontFamily: "Outfit, sans-serif",
                                    fontSize: "14px",
                                    fontWeight: 400,
                                    px: 1.5,
                                    py: 1,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    color: "#111827",
                                    "&:hover": {
                                        backgroundColor: "#F6F8F9",
                                    },
                                }}
                            >
                                <LuEye size={16} style={{ color: "#4B5563" }} />
                                <span>View</span>
                            </MenuItem>
                        }
                        {editAccess &&
                            <MenuItem
                                onClick={handleMenuEdit}
                                sx={{
                                    fontFamily: "Outfit, sans-serif",
                                    fontSize: "14px",
                                    fontWeight: 400,
                                    px: 1.5,
                                    py: 1,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    color: "#111827",
                                    "&:hover": {
                                        backgroundColor: "#F6F8F9",
                                    },
                                }}
                            >
                                <EditIcon style={{ width: 14, height: 14 }} />
                                <span>Edit</span>
                            </MenuItem>
                        }

                    </Menu>

                    <CustomPagination
                        totalCount={ncrList?.totalRecords || 0}
                        pageSize={pageSize}
                        page={pageNumber}
                        onPageChange={handlePageChange}
                        setCurrentPage={setPageNumber}
                        onPageSizeChange={handlePageSizeChange}
                    />

                </Grid>
            </Grid >

            {isAddRisk && <AddNCRLog open={isAddRisk} handleClose={handleAddRisk} ncrLogContact={ncrLogContact} />}
            {isEdit && <EditNCRLog open={isEdit} handleClose={handleEditRiskClose} rowData={selectedData} ncrLogContact={ncrLogContact} />}
            {isView && <ViewNCRLog open={isView} handleClose={handleViewClose} rowData={viewData} />}
        </>
    );
}

export default NcrLogTracker;
